import type { ContentPreviewConfigEntry } from '../../lib/flotiq-client/types/api-response';

import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';

import { updateCTO } from '../../lib/flotiq-client/api-helpers';
import { getTestProps } from '../../lib/helpers';

import Button from '../../components/Button/Button';
import LinkButton from '../../components/LinkButton/LinkButton';
import Panel from '../../components/Panel/Panel';
import Tooltip from '../../components/Tooltip/Tooltip';

import { useContentObject } from '../../hooks/api';
import useSelectedSpace from '../../hooks/useSelectedSpace';

import UserContext from '../../contexts/UserContext';

import { DeleteIcon } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';

const testId = 'content-preview-form';
const PREVIEW_CTD = '_content_preview';
const CO_ID = 'flotiq.content-publishing';

const ContentPreviewForm = () => {
  const { t } = useTranslation();
  const { permissions } = use(UserContext);
  const { buildUrlWithSpace } = useSelectedSpace();

  const { contentTypeName = '' } = useParams();

  const { canUpdate, canCreate, canDelete } = useMemo(
    () => permissions.getCoPermissions(PREVIEW_CTD) || {},
    [permissions],
  );

  const {
    entity: contentObject,
    partialUpdateEntity: partialUpdateContentObject,
  } = useContentObject(PREVIEW_CTD, CO_ID);

  const config: ContentPreviewConfigEntry[] = useMemo(
    () => (contentObject?.config as ContentPreviewConfigEntry[]) ?? [],
    [contentObject],
  );

  const matchedIndex = useMemo(
    () =>
      config.findIndex((entry) =>
        entry.content_types?.includes(contentTypeName),
      ),
    [config, contentTypeName],
  );

  const existingEntry = matchedIndex >= 0 ? config[matchedIndex] : undefined;

  const [showInput, setShowInput] = useState(false);

  const validation = useMemo(
    () =>
      z.object({
        route_template: z.string().min(1, t('SpaceSettings.Errors.MinLength')),
      }),
    [t],
  );

  const form = useAppForm({
    defaultValues: { route_template: existingEntry?.route_template ?? '' },
    validators: { onChange: validation },
    onSubmit: async ({ value }) => {
      const newEntry: ContentPreviewConfigEntry = {
        content_types: existingEntry?.content_types ?? [contentTypeName],
        route_template: value.route_template,
      };

      const newConfig =
        matchedIndex >= 0
          ? config.map((entry, i) => (i === matchedIndex ? newEntry : entry))
          : [...config, newEntry];

      await updateCTO(
        { config: newConfig },
        PREVIEW_CTD,
        partialUpdateContentObject,
        t,
      );
    },
  });

  useEffect(() => {
    if (existingEntry) {
      setShowInput(true);
      form.reset({ route_template: existingEntry.route_template });
    }
  }, [existingEntry, form]);

  const handleSave = useCallback(async () => {
    await form.handleSubmit();
  }, [form]);

  const handleDelete = useCallback(async () => {
    if (matchedIndex < 0) return;
    const newConfig = config.filter((_, i) => i !== matchedIndex);

    const [, , hasErrors] = await updateCTO(
      { config: newConfig },
      PREVIEW_CTD,
      partialUpdateContentObject,
      t,
      true,
    );
    if (hasErrors) return;

    setShowInput(false);
    form.reset({ route_template: '' });
  }, [matchedIndex, config, partialUpdateContentObject, t, form]);

  const isEditingDisabled = !canCreate && !canUpdate;

  return (
    <Panel
      title={t('Global.ContentPreview')}
      isCollapsable={false}
      additionalContainerClasses="py-5 px-4"
      additionalTitleClasses="text-lg"
      additionalChildrenClasses="pt-4"
      open
      {...getTestProps(testId, 'container', 'testId')}
    >
      <span className="dark:text-slate-200">
        {t('ContentDefinition.ContentPreview.Description')}
      </span>

      {!contentObject && (
        <div className={'flex flex-col gap-5 pt-4'}>
          <span className="font-bold dark:text-slate-200">
            {t('ContentDefinition.ContentPreview.Info')}
          </span>
          <LinkButton
            link={buildUrlWithSpace('space-settings')}
            {...getTestProps(testId, 'configure', 'testId')}
          >
            {t('ContentDefinition.ContentPreview.Configure')}
          </LinkButton>
        </div>
      )}

      {contentObject && showInput && (
        <form.AppForm>
          <div className="pt-5 flex flex-col gap-5">
            <div className="flex items-center gap-4.5 md:gap-3">
              <form.AppField name="route_template">
                {(field) => (
                  <field.Input
                    label={t(
                      'ContentDefinition.ContentPreview.PageRouteTemplate',
                    )}
                    disabled={isEditingDisabled}
                    required
                    additionalClasses="flex-1"
                    {...getTestProps(testId, 'route-template', 'testId')}
                  />
                )}
              </form.AppField>

              {canDelete && matchedIndex >= 0 && (
                <Tooltip
                  tooltip={t('Global.Delete')}
                  additionalClasses="inline-block font-normal"
                  tooltipPlacement="topRight"
                >
                  <Button
                    iconImage={
                      <DeleteIcon
                        className={twMerge(
                          'opacity-50 hover:opacity-100',
                          'text-indigo-750 dark:text-slate-200',
                        )}
                      />
                    }
                    buttonColor="borderless"
                    additionalClasses="mt-4.5"
                    additionalIconClasses="w-4.5 md:w-3.5"
                    onClick={handleDelete}
                    {...getTestProps(testId, 'delete-button', 'testId')}
                  />
                </Tooltip>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleSave}
                disabled={isEditingDisabled}
                {...getTestProps(testId, 'save', 'testId')}
              >
                {t('Global.Save')}
              </Button>
            </div>
          </div>
        </form.AppForm>
      )}

      {contentObject && !showInput && (canCreate || canUpdate) && (
        <div className="pt-5">
          <Button
            onClick={() => setShowInput(true)}
            {...getTestProps(testId, 'add', 'testId')}
          >
            {t('Global.Add')}
          </Button>
        </div>
      )}
    </Panel>
  );
};

export default ContentPreviewForm;
